import { getQueryKey } from "@trpc/react-query";
import type {
  UseInfiniteQueryOptions,
  UseQueryOptions,
  UseSuspenseQueryOptions,
} from "@tanstack/react-query";
import { trpc } from "@utils/trpc";

/**
 * Shared query config functions to avoid circular dependencies.
 * These accept downloadManager methods as callbacks instead of importing downloadManager directly.
 */

type QueryFnDataFromConfig<TConfig> = TConfig extends {
  queryFn: (...args: any[]) => infer R;
}
  ? Awaited<R>
  : never;

type QueryKeyFromConfig<TConfig> = TConfig extends {
  queryKey: infer K extends readonly unknown[];
}
  ? K
  : readonly unknown[];

/**
 * Helper types for the “query config factory + spread options” pattern.
 *
 * This keeps option types aligned with TanStack Query (esp. suspense variants)
 * after upgrades, without duplicating generic parameters everywhere.
 */
export type UseQueryOptionsFromConfig<
  TConfig,
  TData = QueryFnDataFromConfig<TConfig>,
> = Omit<
  UseQueryOptions<
    QueryFnDataFromConfig<TConfig>,
    unknown,
    TData,
    QueryKeyFromConfig<TConfig>
  >,
  "queryKey" | "queryFn"
>;

export type UseSuspenseQueryOptionsFromConfig<
  TConfig,
  TData = QueryFnDataFromConfig<TConfig>,
> = Omit<
  UseSuspenseQueryOptions<
    QueryFnDataFromConfig<TConfig>,
    unknown,
    TData,
    QueryKeyFromConfig<TConfig>
  >,
  "queryKey" | "queryFn"
>;

type PageParamFromInfiniteConfig<TConfig> = TConfig extends {
  queryFn: (context: { pageParam: infer P }) => any;
}
  ? P
  : unknown;

export type UseInfiniteQueryOptionsFromConfig<TConfig> = Omit<
  UseInfiniteQueryOptions<
    QueryFnDataFromConfig<TConfig>,
    unknown,
    QueryFnDataFromConfig<TConfig>,
    QueryKeyFromConfig<TConfig>,
    PageParamFromInfiniteConfig<TConfig>
  >,
  "queryKey" | "queryFn"
>;

// Export query key helpers for use across the codebase to ensure consistent keys
export const songsByIdQueryKey = (songIds?: string[]) =>
  getQueryKey(trpc.catalog.songs.getById, songIds);

export const songsByAlbumIdsQueryKey = (albumIds?: string[]) =>
  getQueryKey(trpc.catalog.songs.getByAlbumIds, albumIds);

export const songsByPlaylistIdsQueryKey = (playlistIds: string[]) =>
  getQueryKey(trpc.catalog.songs.getByPlaylistIds, playlistIds);
